import { useEffect, useMemo, useState } from 'react'
import { apiClient } from '~/api/client'
import { bulkUpdateUserRole, type UserRole } from '~/api/users'
import { useAuthStore } from '~/stores/auth-store'
import { type DonutDatum } from '~/components/charts'
import { useToast } from '~/components/ui/toast'
import { useTableSelection } from '~/lib/use-table-selection'
import {
    ROLE_FILL,
    ROLE_ORDER,
    type PendingRole,
    type PendingStatus,
    type UserRow,
    type UserStatus,
} from './constants'

// 사용자/조직/권한 화면의 상태·부수효과(목록 로드·역할/가입상태 변경·일괄 역할 변경·검색/필터·
// 다중선택)와 파생값(KPI·역할 분포·필터 결과)을 모은 훅. 페이지는 이 훅으로 오케스트레이션만 한다.
export function useUsers() {
    const toast = useToast()
    // 비밀번호 재설정은 권한 상승 못지않은 위험 작업이라 superadmin에게만 노출한다(서버도 게이트).
    const isSuperadmin = useAuthStore((s) => s.adminRole) === 'superadmin'
    const [users, setUsers] = useState<UserRow[]>([])
    const [loading, setLoading] = useState(true)
    const [pending, setPending] = useState<PendingRole | null>(null)
    // 비밀번호 재설정 대상 사용자(없으면 모달 닫힘).
    const [pwdTarget, setPwdTarget] = useState<UserRow | null>(null)
    const [statusPending, setStatusPending] = useState<PendingStatus | null>(
        null
    )
    const [query, setQuery] = useState('')
    const [statusFilter, setStatusFilter] = useState<'all' | UserStatus>('all')

    // 다중선택(범용 훅) + 일괄 역할 변경. 적용 전 ConfirmDialog로 한 번 더 확인받는다.
    const selection = useTableSelection<string>()
    const [bulkRole, setBulkRole] = useState<UserRole>('user')
    const [bulkConfirm, setBulkConfirm] = useState(false)
    const [bulkBusy, setBulkBusy] = useState(false)

    const load = () => {
        setLoading(true)
        apiClient
            .get<UserRow[]>('/users')
            .then(setUsers)
            .catch((e) => toast.error((e as Error).message))
            .finally(() => setLoading(false))
    }

    useEffect(load, [])

    const confirmRole = async () => {
        if (!pending) return
        const { user, role } = pending
        setPending(null)
        try {
            await apiClient.put(`/users/${user.id}`, { role })
            toast.success(`${user.email} 역할을 '${role}'로 변경했어요.`)
            load()
        } catch (e) {
            toast.error((e as Error).message)
        }
    }

    // 가입 신청 승인/거부 → PUT /users/{id}/status → 토스트 + 목록 새로고침.
    const confirmStatus = async () => {
        if (!statusPending) return
        const { user, status } = statusPending
        setStatusPending(null)
        try {
            await apiClient.put(`/users/${user.id}/status`, { status })
            toast.success(
                `${user.email} 가입을 ${
                    status === 'active' ? '승인' : '거부'
                }했어요.`
            )
            load()
        } catch (e) {
            toast.error((e as Error).message)
        }
    }

    // 일괄 역할 변경: 선택 ids 전체에 같은 role 적용 → 결과 토스트 + 목록 새로고침 + 선택 해제.
    // 마지막 superadmin 보호 등 거부는 서버가 처리하고, 적용 못 한 건은 실패 수로 표시된다.
    const applyBulkRole = async () => {
        const ids = [...selection.selected]
        setBulkConfirm(false)
        if (ids.length === 0) return
        setBulkBusy(true)
        try {
            const r = await bulkUpdateUserRole(ids, bulkRole)
            selection.clear()
            load()
            toast.success(
                `역할 일괄 변경 완료 · 변경 ${r.updated} · 실패 ${r.failed.length}`,
                { action: { label: '목록 새로고침', onClick: load } }
            )
        } catch (e) {
            toast.error((e as Error).message)
        } finally {
            setBulkBusy(false)
        }
    }

    // KPI: 전체·관리자(admin+superadmin)·조직 수(있을 때만).
    const total = users.length
    const adminCount = users.filter(
        (u) => u.role === 'admin' || u.role === 'superadmin'
    ).length
    const orgs = new Set(
        users.map((u) => u.organization).filter((o): o is string => !!o)
    )
    const hasOrg = orgs.size > 0
    const pendingCount = users.filter((u) => u.status === 'pending').length

    // 역할 분포(있는 역할만 순서대로, 의미색).
    const roleCounts = users.reduce<Record<string, number>>((acc, u) => {
        acc[u.role] = (acc[u.role] ?? 0) + 1
        return acc
    }, {})
    const roleDist: DonutDatum[] = ROLE_ORDER.filter(
        (r) => roleCounts[r] != null
    ).map((r) => ({
        name: r,
        value: roleCounts[r],
        color: ROLE_FILL[r],
    }))

    // 상태 필터 + 이메일/이름 클라이언트 검색(부분 일치, 대소문자 무시).
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase()
        return users.filter((u) => {
            if (
                statusFilter !== 'all' &&
                (u.status ?? 'active') !== statusFilter
            )
                return false
            if (!q) return true
            return (
                u.email.toLowerCase().includes(q) ||
                u.name.toLowerCase().includes(q)
            )
        })
    }, [users, query, statusFilter])

    const empty = users.length === 0

    // 현재 보이는(검색 적용된) 행 id — 전체선택 체크박스 상태/토글 기준.
    const visibleIds = filtered.map((u) => u.id)
    const headState = selection.allState(visibleIds)

    return {
        isSuperadmin,
        loading,
        pending,
        setPending,
        pwdTarget,
        setPwdTarget,
        statusPending,
        setStatusPending,
        query,
        setQuery,
        statusFilter,
        setStatusFilter,
        selection,
        bulkRole,
        setBulkRole,
        bulkConfirm,
        setBulkConfirm,
        bulkBusy,
        load,
        confirmRole,
        confirmStatus,
        applyBulkRole,
        total,
        adminCount,
        orgs,
        hasOrg,
        pendingCount,
        roleDist,
        filtered,
        empty,
        visibleIds,
        headState,
    }
}
